import React, { useState } from "react";
import { useToast } from "../../../../context/ToastContext";
import { useRelationshipPrefillMany } from "../../../../hooks/useRelationshipPrefill";
import { formatDateForInput } from "../../../../utils/formatters";
import { formatSSNInput, countSSNDigits } from "../../../../utils/validationHelpers";
import { RELATIONSHIP_OPTIONS } from "../../../../constants/membersConstants";
import {
  getHtmlDateMinMax,
  normalizeToYmd,
  validatePastOnlyYmd,
} from "../../../../utils/dateFieldConstraints";
import DatePickerInput from "../DatePickerInput";
import { US_STATES } from "../../../../data/usStatesAndCities";
import { resolveStateCode } from "../../../../utils/stateUtils";
import {
  isRevocableTrust,
  getTrusteeMemberId,
  hasDuplicateTrusteeMemberIds,
  isPlaceholderTrusteeRelationship,
  trusteeNeedsRelationshipToGrantor,
  getTrustGrantorIds,
  buildTrustInfoUpdatePayload,
} from "../../../../utils/trustInfoHelpers";

const isMissing = (val) => val === null || val === undefined || val === "";

/** Mandatory trust fields per product spec. */
const REQUIRED_FIELDS = [
  { key: "trust_title", label: "Trust Title (as per document)", type: "text" },
  { key: "ssn_or_tin", label: "SSN or TIN", type: "text", placeholder: "XXX-XX-XXXX" },
  { key: "trust_state", label: "Trust State", type: "state_select" },
  { key: "execution_date", label: "Execution Date", type: "date" },
];

const getMemberSsnDigitsById = (memberId, members) => {
  if (!memberId) return "";
  const member = (members || []).find((m) => m.id === memberId);
  const raw = member?.personal_info?.ssn_or_tin;
  return String(raw || "").replace(/\D/g, "");
};

const getMemberSsnRawById = (memberId, members) => {
  if (!memberId) return "";
  const member = (members || []).find((m) => m.id === memberId);
  return String(member?.personal_info?.ssn_or_tin ?? "");
};

const TrustInfoForm = ({ householdData, fieldData, missingFields, entityId, onSave, onSaveAndExit, saving, nextLabel, clientToken }) => {
  const { toast } = useToast();
  const account = fieldData || {};
  const existing = account.trust_info || {};
  const members = householdData?.members || [];

  const existingGrantorIds = getTrustGrantorIds(existing);
  const showGrantors = existingGrantorIds.length === 0;

  const missingRequiredFields = REQUIRED_FIELDS.filter((f) => {
    if (f.key === "ssn_or_tin") {
      if (isRevocableTrust(account.type)) {
        const grantorIds = getTrustGrantorIds(existing);
        const primaryGrantorId = grantorIds[0] || "";
        return countSSNDigits(getMemberSsnRawById(primaryGrantorId, members)) !== 9;
      }
      return isMissing(existing[f.key]);
    }
    return isMissing(existing[f.key]);
  });
  const showTrustees =
    !Array.isArray(existing.trustees) ||
    existing.trustees.length === 0 ||
    existing.trustees.some((t) => !getTrusteeMemberId(t));

  const [formData, setFormData] = useState(() => {
    const initial = {};
    missingRequiredFields.forEach((f) => {
      if (f.type === "date" && existing[f.key]) {
        initial[f.key] = formatDateForInput(existing[f.key]);
      } else if (f.key === "ssn_or_tin" && existing[f.key]) {
        initial[f.key] = formatSSNInput(String(existing[f.key]));
      } else if (f.type === "boolean_select" && existing[f.key] !== undefined) {
        initial[f.key] = String(existing[f.key]);
      } else {
        initial[f.key] = "";
      }
    });
    if (showGrantors) {
      initial.grantors = [{ id: "" }];
    }
    if (showTrustees) {
      const existingTrustees = Array.isArray(existing.trustees)
        ? existing.trustees.map((t) => ({
            id: getTrusteeMemberId(t) || "",
            relationship_to_grantor: t?.relationship_to_grantor || "",
          }))
        : [];
      initial.trustees =
        existingTrustees.length > 0
          ? existingTrustees
          : [{ id: "", relationship_to_grantor: "" }];
    }
    return initial;
  });

  const currentRevocable = isRevocableTrust(account.type);

  const currentGrantorIds = formData.grantors !== undefined
    ? (formData.grantors || []).map((g) => g.id).filter(Boolean)
    : existingGrantorIds;
  const currentPrimaryGrantorId = currentGrantorIds[0] || "";
  const derivedGrantorSsnDigits = getMemberSsnDigitsById(currentPrimaryGrantorId, members);
  const effectiveSsnDisplay = currentRevocable
    ? formatSSNInput(getMemberSsnRawById(currentPrimaryGrantorId, members))
    : formData.ssn_or_tin || "";

  const currentGrantorIdSet = new Set(currentGrantorIds.map(String));

  const trusteesForValidation = showTrustees
    ? (formData.trustees || [])
    : (Array.isArray(existing.trustees) ? existing.trustees : []);
  const trusteesMissing = !trusteesForValidation.some((t) => getTrusteeMemberId(t));
  const trusteesRelationshipIncomplete =
    showTrustees &&
    (formData.trustees || []).some((t) => {
      const id = getTrusteeMemberId(t);
      if (!id) return false;
      const needsRel = id && !currentGrantorIdSet.has(String(id));
      if (
        !needsRel ||
        !isPlaceholderTrusteeRelationship(t?.relationship_to_grantor)
      ) {
        return false;
      }
      return true;
    });
  const trusteesDuplicates =
    showTrustees && hasDuplicateTrusteeMemberIds(formData.trustees || []);
  const grantorsMissing =
    showGrantors && !(formData.grantors || []).some((g) => g.id);
  const missingCount =
    missingRequiredFields.length +
    (grantorsMissing ? 1 : 0) +
    (trusteesMissing ? 1 : 0) +
    (trusteesRelationshipIncomplete ? 1 : 0) +
    (trusteesDuplicates ? 1 : 0);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTrusteeChange = (idx, key, value) => {
    setFormData((prev) => ({
      ...prev,
      trustees: (prev.trustees || []).map((row, i) =>
        i === idx ? { ...row, [key]: value } : row,
      ),
    }));
  };

  useRelationshipPrefillMany({
    enabled: Boolean(currentPrimaryGrantorId),
    householdId: householdData?.id,
    counterpartyMemberId: currentPrimaryGrantorId,
    clientToken: clientToken || null,
    rows: formData.trustees || [],
    getMemberId: (row) => {
      const id = getTrusteeMemberId(row);
      if (!id || currentGrantorIdSet.has(String(id))) return null;
      return id;
    },
    getCurrentValue: (row) => row?.relationship_to_grantor,
    onPrefill: (idx, relationship) =>
      handleTrusteeChange(idx, "relationship_to_grantor", relationship),
  });

  const addTrusteeRow = () => {
    setFormData((prev) => ({
      ...prev,
      trustees: [...(prev.trustees || []), { id: "", relationship_to_grantor: "" }],
    }));
  };

  const removeTrusteeRow = (idx) => {
    setFormData((prev) => ({
      ...prev,
      trustees: (prev.trustees || []).filter((_, i) => i !== idx),
    }));
  };

  const buildPayload = () => {
    const trust_info = { ...existing };
    delete trust_info.revocable;
    missingRequiredFields.forEach((f) => {
      if (f.key === "ssn_or_tin") {
        const d = String(formData[f.key] || "").replace(/\D/g, "");
        trust_info[f.key] = d.length === 9 ? d : null;
      } else if (f.type === "member_select") {
        trust_info[f.key] = formData[f.key] || null;
      } else if (f.type === "state_select") {
        trust_info[f.key] = resolveStateCode(formData[f.key]) || null;
      } else if (f.type === "date") {
        trust_info[f.key] = formData[f.key] || null;
      } else {
        trust_info[f.key] = formData[f.key]?.trim?.() || null;
      }
    });
    if (currentRevocable) {
      trust_info.ssn_or_tin =
        derivedGrantorSsnDigits.length === 9 ? derivedGrantorSsnDigits : null;
    }
    if (showTrustees) {
      trust_info.trustees = (formData.trustees || [])
        .map((t) => ({
          id: getTrusteeMemberId(t) || null,
          relationship_to_grantor: t?.relationship_to_grantor || null,
        }))
        .filter((t) => t.id);
    }
    const grantorIds = showGrantors
      ? (formData.grantors || []).map((g) => g.id).filter(Boolean)
      : existingGrantorIds;
    const finalPayload = buildTrustInfoUpdatePayload(existing, {
      grantorIds,
      patch: trust_info,
    });
    return { accounts: { [entityId]: { trust_info: finalPayload } } };
  };

  const pastTrustBounds = getHtmlDateMinMax("past_only");

  const handleSubmit = (cb) => {
    if (!currentRevocable && missingRequiredFields.some((f) => f.key === "ssn_or_tin")) {
      if (countSSNDigits(formData.ssn_or_tin || "") !== 9) {
        toast.error("Please enter a valid SSN or TIN (9 digits).");
        return;
      }
    }
    if (
      missingRequiredFields.some((f) => f.key === "execution_date") &&
      formData.execution_date
    ) {
      const msg = validatePastOnlyYmd(
        normalizeToYmd(formData.execution_date),
        "Execution date",
      );
      if (msg) {
        toast.error(msg);
        return;
      }
    }
    if (showGrantors) {
      const hasGrantor = (formData.grantors || []).some((g) => g.id);
      if (!hasGrantor) {
        toast.error("Please select at least one grantor.");
        return;
      }
    }
    if (showTrustees) {
      const rows = formData.trustees || [];
      const hasTrusteeWithId = rows.some((t) => getTrusteeMemberId(t));
      if (!hasTrusteeWithId) {
        toast.error("Please select at least one trustee.");
        return;
      }
      if (hasDuplicateTrusteeMemberIds(rows)) {
        toast.error("Each trustee must be a different household member.");
        return;
      }
      const badRel = rows.find((t) => {
        const id = getTrusteeMemberId(t);
        if (!id) return false;
        const needsRel = id && !currentGrantorIdSet.has(String(id));
        return (
          needsRel &&
          isPlaceholderTrusteeRelationship(t?.relationship_to_grantor)
        );
      });
      if (badRel) {
        toast.error("Please choose relationship to grantor for each trustee who is not the grantor.");
        return;
      }
    }
    cb(buildPayload());
  };

  const getMemberDisplay = (memberId) => {
    if (!memberId) return "";
    const m = members.find((x) => String(x.id) === String(memberId));
    if (!m) return String(memberId);
    return `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim();
  };

  const assignedTrusteeIds = new Set(
    (formData.trustees || [])
      .map((t) => getTrusteeMemberId(t))
      .filter(Boolean)
      .map(String),
  );
  const canAddTrusteeRow =
    members.filter((m) => !assignedTrusteeIds.has(String(m.id))).length > 0;

  // Do not show the "all filled" empty state while trustee or grantor rows are still being
  // collected from the server; otherwise picking a member makes missingCount hit 0
  // before save when other required keys were already present on the account.
  if (missingCount === 0 && !showTrustees && !showGrantors) {
    const grantorIdSet = new Set(getTrustGrantorIds(existing).map(String));
    return (
      <div className="text-center py-6 text-gray-500 space-y-3">
        <p className="text-sm">All trust info has been filled.</p>
        {!showTrustees &&
          Array.isArray(existing.trustees) &&
          existing.trustees.length > 0 && (
            <div className="text-left max-w-md mx-auto rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800">
              <p className="font-medium text-gray-900 mb-2">Trustees on file</p>
              <ul className="list-disc pl-5 space-y-1">
                {existing.trustees.map((t, i) => {
                  const tid = getTrusteeMemberId(t);
                  const isGrantor = tid && grantorIdSet.has(String(tid));
                  return (
                    <li key={tid || i}>
                      {getMemberDisplay(tid) || "—"}
                      {!isGrantor && t?.relationship_to_grantor
                        ? ` — ${t.relationship_to_grantor}`
                        : isGrantor
                          ? " (same as grantor)"
                          : ""}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
      </div>
    );
  }

  const grantorIdSetForDisplay = new Set(getTrustGrantorIds(existing).map(String));

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          ● {missingCount} Required field{missingCount !== 1 ? "s" : ""} missing
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {missingRequiredFields.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} <span className="text-red-500">*</span>
            </label>
            {f.type === "state_select" ? (
              <select value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                <option value="">Select...</option>
                {US_STATES.map((s) => <option key={s.abbreviation} value={s.abbreviation}>{s.name} ({s.abbreviation})</option>)}
              </select>
            ) : f.type === "member_select" ? (
              <select value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                <option value="">Select...</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.personal_info?.first_name} {m.personal_info?.last_name}</option>)}
              </select>
            ) : f.type === "boolean_select" ? (
              <select
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select...</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : f.type === "date" ? (
              <DatePickerInput
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                min={pastTrustBounds.min}
                max={pastTrustBounds.max}
              />
            ) : f.key === "ssn_or_tin" ? (
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={11}
                value={effectiveSsnDisplay}
                onChange={(e) =>
                  !currentRevocable &&
                  handleChange(f.key, formatSSNInput(e.target.value))
                }
                placeholder={f.placeholder || "XXX-XX-XXXX"}
                disabled={currentRevocable}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 tracking-widest disabled:bg-gray-100 disabled:text-gray-500"
              />
            ) : (
              <input type="text" value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} placeholder={f.placeholder || ""} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            )}
            {f.key === "ssn_or_tin" && currentRevocable && (
              <p className="text-xs text-gray-500 mt-1">
                For revocable trusts, SSN/TIN uses the selected grantor&apos;s SSN/TIN.
              </p>
            )}
          </div>
        ))}
        {showGrantors && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Grantors <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {(formData.grantors || []).map((g, idx) => (
                <div key={`grantor-${idx}-${g.id || "empty"}`} className="flex gap-2 items-end">
                  <select
                    value={g.id || ""}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        grantors: (prev.grantors || []).map((r, i) =>
                          i === idx ? { ...r, id: e.target.value } : r,
                        ),
                      }));
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">Select member...</option>
                    {members
                      .filter((m) => !(formData.grantors || []).some((r, i) => i !== idx && String(r.id || "") === String(m.id)))
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.personal_info?.first_name} {m.personal_info?.last_name}
                        </option>
                      ))}
                  </select>
                  {(formData.grantors || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          grantors: (prev.grantors || []).filter((_, i) => i !== idx),
                        }))
                      }
                      className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                    >Remove</button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    grantors: [...(prev.grantors || []), { id: "" }],
                  }))
                }
                disabled={members.filter((m) => !(formData.grantors || []).some((r) => String(r.id || "") === String(m.id))).length === 0}
                className="px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm disabled:opacity-50"
              >+ Add Grantor</button>
            </div>
          </div>
        )}
        {!showTrustees &&
          Array.isArray(existing.trustees) &&
          existing.trustees.length > 0 && (
            <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800">
              <p className="font-medium text-gray-900 mb-2">Trustees on file</p>
              <ul className="list-disc pl-5 space-y-1">
                {existing.trustees.map((t, i) => {
                  const tid = getTrusteeMemberId(t);
                  const isGrantor = tid && grantorIdSetForDisplay.has(String(tid));
                  return (
                    <li key={tid || i}>
                      {getMemberDisplay(tid) || "—"}
                      {!isGrantor && t?.relationship_to_grantor
                        ? ` — ${t.relationship_to_grantor}`
                        : isGrantor
                          ? " (same as grantor)"
                          : ""}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        {showTrustees && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trustees <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {(formData.trustees || []).map((trustee, idx) => (
                <div key={`trustee-${idx}-${trustee.id || "empty"}`} className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Trustee {idx + 1}
                    </label>
                    <select
                      value={trustee.id || ""}
                      onChange={(e) => handleTrusteeChange(idx, "id", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="">Select member...</option>
                      {members
                        .filter((m) => {
                          const selectedInOtherRows = (formData.trustees || [])
                            .some(
                              (row, rowIdx) =>
                                rowIdx !== idx && String(row?.id || "") === String(m.id),
                            );
                          return !selectedInOtherRows;
                        })
                        .map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.personal_info?.first_name} {m.personal_info?.last_name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Relationship to Grantor
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={trustee.relationship_to_grantor || ""}
                        onChange={(e) =>
                          handleTrusteeChange(
                            idx,
                            "relationship_to_grantor",
                            e.target.value,
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="">Select relationship...</option>
                        {RELATIONSHIP_OPTIONS.map((rel) => (
                          <option key={rel} value={rel}>
                            {rel}
                          </option>
                        ))}
                      </select>
                      {(formData.trustees || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTrusteeRow(idx)}
                          className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addTrusteeRow}
                disabled={!canAddTrusteeRow}
                title={
                  canAddTrusteeRow
                    ? ""
                    : "Every household member is already assigned as a trustee."
                }
                className="px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                + Add Trustee
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">Save & Exit</button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default TrustInfoForm;
